import Cookies from 'js-cookie';

import api from '@/api/index';
import type { CartListsItem } from '@/api/types/cart';
import type {
  ApplyBffPcGlobalDataPayload,
  BffLayoutCsrState,
  BffLayoutSsrState,
  BffPcGlobalData,
} from '@/api/types/common';
import type { IPriceInfo } from '@/api/types/currency-price';
import { setLocal, setSession } from '@/assets/js/common';
import { setFbq, updateUserData } from '@/assets/js/fbq';
import { PAY_METHODS } from '@/assets/js/order';
import { checkAcc } from '@/assets/js/product';
import setTtq from '@/assets/js/tt-api';
import {
  accMul,
  add,
  getSetLocalStorage,
  getSetSessionStorage,
  randomString,
  visitor,
} from '@/assets/js/utils';
import { getCartIds } from '@/views/shopping-cart/modules/clip';
import { generateEventId } from '~/common/stape';

import type {
  AddCartParams,
  DeleteCartParams,
  LoginParams,
  Store,
  StoreActions,
} from './types/actions';
import type { SecondDiscountRule } from './types/state';

type BffTryonImgItem = NonNullable<BffPcGlobalData['tryon_img']>[number];

let cartPriceInfoRefreshSeq = 0;

/**
 * @description: 根据多币种价格列表获取当前币种的价格信息
 * @param priceList(Array)
 * @return Object
 */
// eslint-disable-next-line vooglam-local/max-optional-params
const genPrice = function (priceList?: IPriceInfo[], currencyName: string = 'USD') {
  try {
    const price = priceList?.find((item) => item.currency === currencyName);
    if (price) {
      if (price.sell_price && price.price != price.sell_price) {
        price.discount = true;
      }
      return price;
    }
  } catch (error) {
    console.log(error, priceList);
  }
  return { price: '', sell_price: '' };
};

/**
 * @description: 获取单品价格（只用于购物车列表中的数据）
 * @param item 商品信息（购物车列表中的一项）
 * @param isOrigin 是否是原价，默认 false
 * @param currencyName 指定币种
 * @return 价格字符串，保留 2 位小数
 */
// eslint-disable-next-line vooglam-local/max-optional-params
export function genItemPrice(item: CartListsItem, isOrigin = false, currencyName?: string): string {
  try {
    let framePrice: string | number = 0;
    let lensPrice: string | number = 0;
    let coatingPrice: string | number = 0;
    let clipPrice = 0;
    if (item.product?.price_info) {
      const fp = genPrice(item.product.price_info, currencyName);
      framePrice = (isOrigin ? fp.price : fp.sell_price) ?? 0;
    }
    if (item.lens?.price_info) {
      const lp = genPrice(item.lens.price_info, currencyName);
      lensPrice = (isOrigin ? lp.price : lp.sell_price) ?? 0;
    }
    if (item.coating?.price_info) {
      const cp = genPrice(item.coating.price_info, currencyName);
      coatingPrice = (isOrigin ? cp.price : cp.sell_price) ?? 0;
    }
    if (item.clips?.length) {
      item.clips.forEach((clip) => {
        const clipP = genPrice(clip.price_info ?? [], currencyName);
        const unit = isOrigin ? clipP.price : clipP.sell_price;
        clipPrice += Number(unit) * (clip?.qty ?? 0);
      });
    }
    return add(
      accMul(add(add(framePrice, lensPrice), coatingPrice), item.qty ?? 0),
      clipPrice,
    ).toFixed(2);
  } catch (error) {
    console.log(error);
  }
  return '';
}

/* 置顶国家排序 */
const commonCountry: string[] = ['US', 'CA', 'GB', 'FR', 'DE', 'AU', 'IT'];
/* 国家字母排序 */
const indexList: string[] = [
  '#',
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'O',
  'P',
  'Q',
  'R',
  'S',
  'T',
  'U',
  'V',
  'W',
  'X',
  'Y',
  'Z',
  '?', // 兼容奥兰群岛
];
const countrySort = function (list: any[]): any[] {
  if (!Array.isArray(list)) {
    return [];
  }
  const sortList = indexList.map((index) => ({ key: index, value: [] as any[] }));
  list.forEach((item) => {
    sortList.forEach((index) => {
      if (item.initial === index.key) {
        index.value.push(item);
      }
    });
  });
  sortList[0].value.sort((itemA, itemB) => {
    const a = commonCountry.findIndex((name) => itemA.country_id === name);
    const b = commonCountry.findIndex((name) => itemB.country_id === name);
    // Ps: 原写法 a>b 不会正确更改顺序。改为下面这样的正确写法。
    return a - b;
  });
  return sortList.flatMap((index) => index.value);
};

const actions: StoreActions & ThisType<Store> = {
  async collectError(params: any) {
    try {
      if (process.client) {
        let userId = localStorage.getItem('voo-userId');
        if (userId) {
          userId = JSON.parse(userId);
          params.user_id = userId;
        }
        params.referrer = document.referrer;
        params.href = location.href;
        params.time = Date.now();
        await api.common.collectError({ json_error: JSON.stringify(params) });
      }
    } catch (error) {
      console.log('collectError error', error);
    }
  },
  async getCountry() {
    try {
      const { data } = await api.common.countryList();
      const list = countrySort(data);
      this.SET_COUNTRY_LIST(list);
    } catch (error) {
      console.log('getCountry error', error);
      this.collectError({
        error,
        api_path: '/api/public/country',
      });
    }
  },
  getVisitorId() {
    return new Promise((resolve, reject) => {
      this.SET_VISITORID(visitor());
      resolve();
    });
  },
  async toLogin(params: LoginParams) {
    if (!params?.email) {
      return Promise.reject(new Error('Email is required'));
    }
    const { data } = await api.login.login(
      {
        ...params,
      },
      params.duration,
    );
    // 是否记住密码
    if (params.remember) {
      localStorage.setItem('voo-temp-infor', JSON.stringify({ email: params.email }));
    } else {
      localStorage.removeItem('voo-temp-infor');
    }
    // gtag event
    window.gtag?.('event', 'login', {
      method: 'web-desktop',
    });
    await this.afterLogin({ data, type: 'LOGIN' });
  },
  logOut(isTokenExpired: boolean = false) {
    if (!isTokenExpired && this.token) {
      api.login.logout();
    }

    this.EmarsysLogin();
    this.SET_UNCHECK_LIST([]);
    this.SET_IS_FIRST_ENTER_CART(true);
    return new Promise((resolve, reject) => {
      // 无论是否成功执行登出接口，500毫秒后都清空登录数据
      setTimeout(() => {
        try {
          this.SET_CARTINFOR({
            list: [],
            can_not_sell_list: [],
          });
          Cookies.remove('voo-token');
          this.SET_TOKEN('');
          this.SET_IS_RIGHT_NAV_CLICK(false);
          this.SET_USER_INFO({ point: 0, wish_num: 0 });
          getSetSessionStorage.remove('voo-utm-review-flag');

          // 清除存储的分享获得积分记录
          ['Facebook', 'Email', 'Copy'].forEach((key) => {
            localStorage.removeItem(`voo-sharePoint__${key}`);
          });
          // 清除已绑定用户的试戴图片
          const firstModel = this.modelList[0];
          if (firstModel && firstModel.id) {
            this.DEL_MODELLIST(firstModel);
          }
          this.getVisitorId();
          resolve(undefined);
        } catch (err) {
          reject(err);
        }
      }, 500);
    });
  },
  async toRegister(params: any) {
    const apiData = {
      mw_reward: this.reward,
      ...params,
    };
    // 优先使用传递的channel，同时设置一个默认值111（表示未特别指定的快捷注册）
    apiData.channel = params.channel || 111;
    // impact
    if (getSetLocalStorage.get('irclickid')) {
      apiData.irclickid = getSetLocalStorage.get('irclickid');
    }

    // 跟正晖确认：快捷注册和普通注册在接口层面已经没有区别。暂保持现状不变。
    const { data } = await api.login[apiData.isNormal ? 'register' : 'fastRegister'](apiData, {
      errorMsg: params.errorMsg ?? false,
      successMsg: params.successMsg ?? true,
      isEncrypt: true,
    });

    if (['decline', 'review'].includes(data.verify_state)) {
      return { showReCaptcha: true };
    }

    /* 统计代码 */
    // 生成统一 eventId，供 TikTok、Facebook、Stape (LoginSuccessNew_Result→sign_up) 共用，确保三方去重一致
    const registerEventId = generateEventId();

    // Stape 神策上报：将统一 eventId 传给 Stape sign_up 事件
    window.voo_sensors.sensorsTrack('LoginSuccessNew_Result', {
      is_new: true,
      login_method: params.isNormal ? 'email' : 'quick',
      account_type: '邮箱账号',
      account_platform: 'pc',
      page_name: window.location.href,
      url_path: window.location.href,
      event_id: registerEventId,
    });

    /* tiktok */

    window.ttq?.identify({
      email: data.customer.email || '',
      phone_number:
        data.customer.tel_code && data.customer.telephone
          ? `+${data.customer.tel_code}${data.customer.telephone}`
          : '',
    });

    window.ttq?.track('CompleteRegistration', {}, { event_id: registerEventId });
    setTtq('CompleteRegistration', registerEventId);
    // gtag event
    window.gtag?.('event', 'sign_up', {
      method: 'web-desktop',
    });
    // google注册增强型转化代码
    window.gtag?.('set', 'user_data', { email: data.customer.email || '' });
    window.gtag?.('event', 'conversion', { send_to: 'AW-606805547/u_1zCNi-n9oDEKu8rKEC' });
    // bing 注册 - 使用统一的 registerEventId
    window.uetq?.push('event', 'signup', { event_id: registerEventId });
    // rtb house
    window.rtbhEvents?.push({
      eventType: 'custom',
      name: 'register_form_submitted',
      value: '1',
    });
    /* 统计代码end */
    await this.afterLogin({ data, type: 'REGISTER' });

    // fb像素 放到获取用户信息之后，确保可以拿到用户邮箱
    // 传入与 TikTok 相同的 registerEventId，保证三方 event_id 一致
    setFbq('CompleteRegistration', { event_id: registerEventId });

    return data;
  },
  /**
   * @description: Emarsys 登录登出操作
   * @prarms customerId, 如果有值执行登录操作，如果没有值执行登出操作
   */
  EmarsysLogin(customerId?: string) {
    try {
      const fieldId = 708;
      if (customerId && !localStorage.getItem('emarsysLogin')) {
        // for user in login status
        window.WebEmarsysSdk?.push(() => {
          window.WebEmarsysSdk?.isSubscribed()
            .then(() => {
              const contactInfo = {
                fieldId: Number(fieldId),
                fieldValue: `${customerId}`,
              };
              window.WebEmarsysSdk?.login(contactInfo).then((res) => {
                if (res) {
                  localStorage.setItem('emarsysLogin', 'login');
                }
              });
            })
            .catch((err) => {
              console.error('Is subscribed error', err);
            });
        });
      } else if (localStorage.getItem('emarsysLogin')) {
        // for visitor (anonymous)
        window.WebEmarsysSdk?.push(() => {
          window.WebEmarsysSdk?.isSubscribed()
            .then(() => {
              window.WebEmarsysSdk?.logout().then((res) => {
                if (res) {
                  localStorage.removeItem('emarsysLogin');
                }
              });
            })
            .catch((err) => {
              console.error('Is subscribed error', err);
            });
        });
      }
    } catch (error) {
      console.log('Emarsys login', error, customerId);
    }
  },
  async afterLogin({ data, type }: { data: any; type?: string }) {
    this.SET_REWARD('');
    this.SET_UNCHECK_LIST([]);
    // 游客点击checkout按钮触发的登录，保持购物车非赠品勾选项不变（赠品由接口推荐）
    const loginType = sessionStorage.getItem('voo-loginType');
    if (loginType === 'checkout') {
      this.SET_IS_FIRST_ENTER_CART(false);
    } else {
      this.SET_IS_FIRST_ENTER_CART(true);
    }

    // 检查是否为游客首次登录（登录前有visitorId）
    if (this.visitorId) {
      localStorage.setItem('voo-guest-first-login', 'true');
    }

    this.SET_TOKEN(data.token);
    this.SET_VISITORID('');
    /* 登录、注册之后立即存一下用户信息，有的地方需要登录之后立即用到 */
    if (data?.customer?.id) {
      this.SET_USER_INFO(data.customer);
      this.EmarsysLogin(data?.customer?.id);
    }
    this.getVipEquity();
    if (loginType !== 'checkout') {
      this.getCart();
    }
    sessionStorage.removeItem('voo-loginType');
    this.getPrescriptionList(); // 获取用户的处方信息
    await this.getUserInfo();
    if (this.userInfo?.id) {
      this.EmarsysLogin(this.userInfo?.id);
    }
    // 获取用户曾经上传的试戴图
    // 如果用户的试戴列表有本地图片，则跟当前用户绑定
    const firstModel = this.modelList[0];
    if (firstModel && firstModel.user && !firstModel.id) {
      try {
        const formData = new FormData();
        formData.append('image', firstModel.sImg);
        formData.append('site_id', '2');
        formData.append('customer_id', String(this.userInfo.id));
        formData.append('customer_email', this.userInfo.email);
        const {
          data: { data: data2 },
        } = await api.tryon.bindImg(formData);
        firstModel.id = data2.id;
      } catch (error) {
        console.log(error);
      }
    } else {
      await this.getUserModel();
    }
    // 用户账户被删除，重新登陆之后，在用户没有刷新浏览器的情况下，如果token失效，401的token过期提示需要再次提示
    this.SET_ALREADY_SHOW_ERR(false);
    window.EmarsysTrack?.track('setCustomerId', `${this.userInfo?.id}`);
    if (type === 'LOGIN') {
      window.EmarsysTrack?.tag('Login_Success');
    } else {
      window.EmarsysTrack?.tag('Register_Success');
    }
  },
  getUtmInfo(path: string = 'fast_register') {
    if (process.server) {
      return {};
    }
    const latestChannel = window.latestChannelAttribute;
    const pub_zl_utm_source =
      latestChannel && latestChannel.expires > Date.now()
        ? latestChannel.pub_zl_utm_source || undefined
        : undefined;
    const pub_zl_utm_medium =
      latestChannel && latestChannel.expires > Date.now()
        ? latestChannel.pub_zl_utm_medium || undefined
        : undefined;
    const pub_zl_utm_campaign =
      latestChannel && latestChannel.expires > Date.now()
        ? latestChannel.pub_zl_utm_campaign || undefined
        : undefined;
    const pub_zl_referrer =
      latestChannel && latestChannel.expires > Date.now()
        ? latestChannel.pub_zl_referrer || undefined
        : undefined;
    const utm_info = {
      pub_zl_utm_source,
      pub_zl_utm_medium,
      pub_zl_utm_campaign,
      pub_zl_referrer,
      path,
    };
    return utm_info;
  },
  thirdPartyLogin(params: any) {
    if (['/resetPassword', '/forgotPassword', '/register'].includes(params.path)) {
      params.path = '/';
    }
    this.provider = params.provider;
    const pathUrl = params.path || '/';
    const { utm_source, utm_id, utm_medium, utm_term } = params.query;
    if (JSON.stringify(params.query) != '{}') {
      params.query = JSON.stringify(params.query);
    } else {
      params.query = '';
    }
    let parameter = '';
    if (pathUrl == '/result') {
      parameter = params.query || '';
    }
    // 如果连接中携带utm领券参数，则参数保留，登录注册后领取相应的券
    if (utm_source && utm_id && utm_medium && utm_term) {
      parameter = params.query || '';
    }

    const channelMap: Record<string, number> = {
      google: 105,
      facebook: 106,
      apple: 107,
    };

    api.login
      .thirdRedirect({
        redirect: pathUrl,
        parameter,
        provider: params.provider,
        mv_reward: this.reward,
        channel: channelMap[params.provider],
        rerequest: this.thirdPartyLoginreRequest,
        utm_info: this.getUtmInfo(),
      })
      .then((res) => {
        location.href = res.data.target_url;
      });
  },
  async thirdPartyLoginCallBack(params: any) {
    await this.afterLogin({ data: params, type: params.isNew === '1' ? 'REGISTER' : 'LOGIN' });
    const vooLoginMethod = localStorage.getItem('voo_login_method');
    const obj = vooLoginMethod ? JSON.parse(vooLoginMethod) : {};
    const channelMap: Record<string, string> = {
      '105': 'google',
      '106': 'fb',
      '107': 'apple',
    };
    const loginType = channelMap[params.channel];
    const event_id = generateEventId();
    // 登录埋点
    window.voo_sensors.sensorsTrack('LoginSuccess_Result', {
      is_new: params.isNew == 1,
      login_situation: '第三方登录',
      url_path: window.location.href,
      login_type: loginType,
      event_id,
    });
    window.voo_sensors.sensorsTrack('LoginSuccessNew_Result', {
      is_new: params.isNew === '1',
      login_situation: obj?.login_situation,
      login_method: obj?.login_method,
      account_type: '三方账号',
      account_platform: loginType,
      page_name: obj?.page_name,
      url_path: window.location.href,
      event_id,
    });
    // gtag event
    if (params.isNew != 1) {
      window.gtag?.('event', 'login', {
        method: this.provider,
      });
    } else {
      // 注册埋点
      try {
        window.ttq?.identify({
          email: this.userInfo.email || '',
          phone_number:
            this.userInfo.tel_code && this.userInfo.telephone
              ? `+${this.userInfo.tel_code}${this.userInfo.telephone}`
              : '',
        });
        const ttqEventId = event_id;
        window.ttq?.track('CompleteRegistration', {}, { event_id: ttqEventId });
        setTtq('CompleteRegistration', event_id);
        window.gtag?.('event', 'sign-up', {
          method: this.provider,
        });
        window.gtag?.('set', 'user_data', { email: this.userInfo.email || '' });
        window.gtag?.('event', 'conversion', { send_to: 'AW-606805547/u_1zCNi-n9oDEKu8rKEC' });
        window.uetq?.push('event', 'signup', { event_id });
        window.rtbhEvents?.push({
          eventType: 'custom',
          name: 'register_form_submitted',
          value: '1',
        });
        setFbq('CompleteRegistration', { event_id });
      } catch (error) {
        console.error('thirdPartyLoginCallBack error', error);
      }
    }
    localStorage.removeItem('voo_login_method');
  },
  /**
   * 获取购物车信息
   * @param {Array} checkedCartIds 勾选且仅勾选的购物车id列表，用于处理从ig小店跳转过来的购物车数据
   */
  async getCart({ checkedCartIds = [] }: { checkedCartIds?: string[] } = {}) {
    try {
      const { data } = await api.cart.cartList();
      const list = data?.list || [];
      const cartPriceRequestSeq = ++cartPriceInfoRefreshSeq;
      const emarsysArr: any[] = [];

      /* 购物车页面，点击checkout按钮登录的，如果购物车没有变化则跳转到payment页面，有变化则保留原勾选商品，(不跳转.
            voo-checkedIds 即为存储在session中的原勾选项 */
      let onlyChecked: string[] | null = null;
      // let unchecked = null;
      if (sessionStorage.getItem('voo-checkedIds')) {
        try {
          const checkedIds = sessionStorage.getItem('voo-checkedIds');
          if (checkedIds) {
            onlyChecked = JSON.parse(checkedIds);
          }
          sessionStorage.removeItem('voo-checkedIds');
        } catch (error) {
          console.log(error);
        }
        // unchecked = [];
      }
      data.list.forEach((item: any) => {
        if (onlyChecked) {
          item.selected = onlyChecked.includes(item.cart_id);
          // if (!item.selected) {
          //   unchecked.push(item.cart_id);
          // }
        } else if (checkedCartIds?.length > 0) {
          item.selected = checkedCartIds.includes(item.cart_id);
        } else {
          item.selected = !this.uncheckList.includes(item.cart_id);
        }
        if ((this.isFirstEnterCart || checkedCartIds?.length > 0) && item.type == 3) {
          item.selected = false;
        }
        item.activeQty = item.qty; // 存储一下从接口获取的数量，修改数量时使用
        item.showdel = false; // 删除弹窗是否展示
        item.showdelload = false; // 删除时的加载效果
        // 先读取localStorage中存储的lenseShow状态
        const lenseShowData = localStorage.getItem('voo-lenseShow');
        const lenseShowObj = lenseShowData ? JSON.parse(lenseShowData) : {};
        item.lenseShow = lenseShowObj[item.cart_id] || false;
        item.colorShow = false; // 修改颜色
        item.showmax = 'show'; // 计数器是否要用到最大值，在修改数量大小时使用
        // 计数器=限购数量（最大可购买数量）时，点击【+】toast 提示,这时+无需被禁用，所以该情况下不需要最大值去限制
        if (item.product?.limit_buy > 0 && item.product?.limit_buy == item.activeQty) {
          item.showmax = 'hide';
        }

        const cartData = item;
        emarsysArr.push({
          item: cartData.product?.shop_sku, // 购物车ID
          quantity: cartData.qty, // 数量
          price: parseFloat(genItemPrice(cartData, false, 'USD')), // 以美元为基础的售价
        });
      });

      data.can_not_sell_list.forEach((item: any) => {
        item.activeQty = item.qty; // 存储一下从接口获取的数量，修改数量时使用
        item.showdel = false;
        item.showdelload = false;
      });
      const _uncheckList = data.list
        .filter((item: any) => !item.selected)
        .map((item: any) => item.cart_id);
      this.SET_UNCHECK_LIST(_uncheckList);
      this.SET_CARTINFOR(data);
      if (list.length) {
        const cartIds = getCartIds(list);
        const currencyName = this.currency.name;
        // 这里特意不用 await 阻塞接口请求。
        // 详情参考 views/shopping-cart/docs/actions全局购物车计价逻辑梳理.md
        api.cartTs
          .getCartPrice({
            currency: currencyName,
            cart_ids: cartIds || [],
            apply_discount: 1,
            is_display_wap_ab: 1,
            default_cart: 1,
          })
          .then(({ data: priceData }) => {
            if (cartPriceRequestSeq === cartPriceInfoRefreshSeq) {
              this.SET_CART_PRICE_INFO(priceData);
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
      window.EmarsysTrack?.track('cart', emarsysArr);

      return Promise.resolve(data);
    } catch (error) {
      console.log(error);
    }
  },
  // 加入购物车
  async addToCart(params: AddCartParams) {
    try {
      const { data } = await api.cart.addCart(params);
      /* tiktok */

      window.ttq?.identify({
        email: this.userInfo.email || '',
        phone_number:
          this.userInfo.tel_code && this.userInfo.telephone
            ? `+${this.userInfo.tel_code}${this.userInfo.telephone}`
            : '',
      });
      // 生成标准 eventId，供 TikTok 客户端和服务端、Facebook、Stape 共用，确保三方去重一致
      // 优先从传入的 params 中获取统一生成的 event_id
      const addToCartEventId = params.event_id || generateEventId();

      // Facebook 像素上报，使用与 TikTok/Stape 相同的 eventId
      setFbq('AddToCart', {
        content_ids: [`${params.product_id}`],
        content_type: 'product',
        value: params.product_price || '',
        currency: params.currency || '',
        event_id: addToCartEventId,
      });

      window.ttq?.track(
        'AddToCart',
        {
          contents: [
            {
              content_id: params?.product_id,
              content_type: 'product',
              content_name: params?.content_name || '',
              price: params?.product_price || '',
            },
          ],
        },
        { event_id: addToCartEventId },
      );
      setTtq(
        'AddToCart',
        addToCartEventId,
        [
          {
            content_id: `${params?.product_id}`,
            content_type: 'product',
            content_name: params?.content_name || '',
            price: params?.product_price || '',
          },
        ],
        params.currency || '',
        params.product_price || '',
      );
      // bing - 使用统一的 addToCartEventId
      window.uetq?.push('event', 'add_to_cart', { event_id: addToCartEventId });
      // rtb house - 使用统一的 addToCartEventId
      window.rtbhEvents?.push(
        {
          eventType: 'basketadd',
          offerId: `${params.product_id}`,
        },
        { eventType: 'uid', id: `${this.userId || 'unknown'}` },
      );
      return Promise.resolve(data);
    } catch (error) {
      console.log('addtocart error', error);
      return Promise.reject(error);
    }
  },
  // 购物车删除商品
  async delFromCart(params: DeleteCartParams) {
    try {
      const res = await api.cart.deleteCart(params);
      return Promise.resolve(res);
    } catch (error) {
      console.log(error);
      return Promise.reject(error);
    }
  },
  // 更新购物车数量-本地
  updateCartLocal(params: any) {
    this.SET_CARTINFOR(params);
  },
  // 添加收藏（心愿单）
  addToWish(params: any) {
    return api.account.wish.addWish(params).then((res) => {
      /* tiktok */

      window.ttq?.identify({
        email: this.userInfo.email || '',
        phone_number:
          this.userInfo.tel_code && this.userInfo.telephone
            ? `+${this.userInfo.tel_code}${this.userInfo.telephone}`
            : '',
      });
      // 生成标准 eventId，供 TikTok 客户端和服务端、Facebook 共用，确保三方去重一致
      // 优先从传入的 params 中获取统一生成的 event_id
      const addToWishEventId = params.event_id || generateEventId();

      // Facebook 像素上报，使用与 TikTok 相同的 eventId
      setFbq('AddToWishlist', {
        content_ids: [`${params.product_id}`],
        content_type: 'product',
        event_id: addToWishEventId,
      });

      window.ttq?.track('AddToWishlist', {}, { event_id: addToWishEventId });
      setTtq('AddToWishlist', addToWishEventId);
      // rtb house - 使用统一的 addToWishEventId
      window.rtbhEvents?.push(
        {
          eventType: 'wishlist',
          offerId: `${params.product_id}`,
        },
        { eventType: 'uid', id: `${this.userId || 'unknown'}` },
      );
      this.getUserInfo();
      return res;
    });
  },
  // 删除收藏（心愿单）
  delFromWish(params: any) {
    return api.account.wish
      .delWish({
        ...params,
      })
      .then((res) => {
        this.getUserInfo();
        return res;
      });
  },
  // 和 wap 端逻辑保持一致
  async addToWishForProductWishButton(params: any) {
    if (params.is_wish == 2) {
      await api.account.wish.delete({
        product_id: parseInt(params.product_id),
      });
    } else if (params.is_wish == 1) {
      await api.account.wish.add({
        product_id: parseInt(params.product_id),
      });
      window.ttq?.identify({
        email: this.userInfo.email || '',
        phone_number:
          this.userInfo.tel_code && this.userInfo.telephone
            ? `+${this.userInfo.tel_code}${this.userInfo.telephone}`
            : '',
      });
      // 生成标准 eventId，供 TikTok 客户端和服务端、Facebook 共用，确保三方去重一致
      // 优先从传入的 params 中获取统一生成的 event_id
      const addWishEventId = params.event_id || generateEventId();

      // Facebook 像素上报，使用与 TikTok 相同的 eventId
      setFbq('AddToWishlist', {
        content_ids: [`${params.product_id}`],
        content_type: 'product',
        event_id: addWishEventId,
      });

      window.ttq?.track('AddToWishlist', {}, { event_id: addWishEventId });
      setTtq('AddToWishlist', addWishEventId);
      // rtb house
      window.rtbhEvents?.push(
        { eventType: 'wishlist', offerId: `${params.product_id}` },
        { eventType: 'uid', id: `${this.userId || 'unknown'}` },
      );
    }
    this.getUserInfo();
  },
  setCurrencyList(list: any[]) {
    this.SET_CURRENCY_LIST(list);
    const currencyTemp = localStorage.getItem('voo-currency');
    const localCurrency = currencyTemp ? JSON.parse(currencyTemp) : {};

    // 如果本地没有缓存的货币设置，则进行新的货币设置
    if (!localCurrency?.name) {
      // 检测是否为搜索引擎爬虫
      // Google 爬虫 (Googlebot)
      // Google 2 爬虫 (google2)
      // Bing 爬虫 (bingbot)
      // Criteo 爬虫 (criteo)
      // Impact 爬虫 (impact)
      // SAS 爬虫 (sas)
      // CJ 爬虫 (cj)
      // Review 爬虫 (review)
      // Facebook 爬虫 (facebookexternalhit)
      // Product 爬虫 (product)
      // TikTok 爬虫 (tiktok)
      // Facebook 国家爬虫 (facebook_country)
      // Facebook 语言爬虫 (facebook_language)
      // Emarsys 爬虫 (emarsys)
      // Pinterest 爬虫 (pinterest)
      // Snapchat 爬虫 (snapchat)
      // Google Shopping/Merchant Center 爬虫 (StoreBot-Google)
      const isCrawler =
        process.client &&
        (/Googlebot/i.test(navigator.userAgent) ||
          /google/i.test(navigator.userAgent) ||
          /bingbot/i.test(navigator.userAgent) ||
          /criteo/i.test(navigator.userAgent) ||
          /impact/i.test(navigator.userAgent) ||
          /sas/i.test(navigator.userAgent) ||
          /cj/i.test(navigator.userAgent) ||
          /review/i.test(navigator.userAgent) ||
          /facebookexternalhit/i.test(navigator.userAgent) ||
          /product/i.test(navigator.userAgent) ||
          /tiktok/i.test(navigator.userAgent) ||
          /facebook_country/i.test(navigator.userAgent) ||
          /facebook_language/i.test(navigator.userAgent) ||
          /emarsys/i.test(navigator.userAgent) ||
          /pinterest/i.test(navigator.userAgent) ||
          /snapchat/i.test(navigator.userAgent) ||
          /StoreBot-Google/i.test(navigator.userAgent) ||
          /Google-Shopping/i.test(navigator.userAgent) ||
          /Google-Update-Web-Shopping/i.test(navigator.userAgent) ||
          /Google-Ads-Overview/i.test(navigator.userAgent));

      // 根据访问者类型设置不同的货币
      if (isCrawler) {
        // 所有爬虫：优先使用 USD，找不到则使用第一个币种
        const usdCurrency = list?.find((item) => item.name === 'USD');
        this.SET_CURRENCY(usdCurrency || list[0]);
      } else {
        // 普通用户：优先使用默认币种，找不到则使用第一个币种
        const defaultCurrency = list?.find((item) => item.is_default === 1);
        this.SET_CURRENCY(defaultCurrency || list[0]);
      }
    }
  },
  async getPrescriptionList() {
    if (!this.token) return;
    try {
      const { data } = await api.account.prescription.list();
      data.list.forEach((item: any, index: number) => {
        item.prescription.prescription_type = item.prescription_type;
      });
      this.SET_PRESCRIPTION_LIST(data.list);
    } catch (error) {
      console.log(error);
    }
  },
  async getAddressList() {
    try {
      const { data } = await api.account.address.list();
      data.list.forEach((item: any, index: number) => {
        if (item.status == 1) {
          item.canclick = true;
        } else {
          item.canclick = false;
        }
      });
      this.SET_ADDRESS_LIST(data.list);
    } catch (error) {
      console.log(error);
    }
  },
  async getUserInfo() {
    try {
      const { data } = await api.account.info.get();
      this.SET_USER_INFO(data);
    } catch (error) {
      console.log(error);
    }
  },
  // 问卷列表
  async getSurveyList() {
    try {
      const { data } = await api.account.survey.getSurveyList();
      this.SET_SURVEY_LIST(data);
    } catch (error) {
      console.log(error);
    }
  },
  async getSurveyPop() {
    try {
      const { data } = await api.account.survey.getSurveyPop();
      this.SET_SURVEY_POP_INFO(data);
    } catch (error) {
      console.log(error);
    }
  },
  // 获取会员权益
  async getVipEquity() {
    const { data } = await api.account.vipEquity.getVipEquity();
    this.SET_VIP_EQUITY(data);
  },
  // 获取各状态订单数量
  async getorderNum() {
    if (!this.token) {
      this.SET_ORDER_NUM({});
      return;
    }
    const { data } = await api.order.statistics();
    this.SET_ORDER_NUM(data);
  },
  async getBlock(params: any) {
    try {
      const { data } = await api.index.indexBlock(params);
      return data;
    } catch (error) {
      console.log(error);
    }
  },
  async getRecommendData(params: any) {
    try {
      const { data } = await api.common.recommend(params);
      Object.keys(data).forEach((key) => {
        if (data[key] && data[key].length) {
          checkAcc(data[key], 4);
        }
      });
      return data;
    } catch (error) {
      console.log(error);
    }
  },
  setCountDownCoupon(data: any) {
    this.SET_COUNT_DOWN_COUPON(data);
  },
  // 获取魔方侧上传的模特图
  async getModelListTemplate() {
    try {
      const { data } = await api.index.indexBlock({
        block_type: 125,
      });
      if (data && data.tryon_img && data.tryon_img.pc_data) {
        const { pc_data } = data.tryon_img;
        const list = pc_data.map((item: any) => {
          return {
            sImg: item.img_url,
            bImg: item.img_url,
            data: item.data,
          };
        });
        this.SET_MODEL_TEMPLATE_LIST(list);
      }
    } catch (error) {
      console.log(error);
    }
  },
  async getUserModel() {
    try {
      // 没有用户id或邮箱，就不需要调接口了
      if (this.userInfo.id && this.userInfo.email) {
        const {
          data: { data },
        } = await api.tryon.getImg({
          site_id: 2,
          customer_id: this.userInfo.id,
          customer_email: this.userInfo.email,
        });
        if (data && data.data && data.img_web_url) {
          // 考虑到老版本用户本地会有多张试戴图片，这里统一删除
          this.modelList.forEach((model) => {
            if (model.user) {
              this.DEL_MODELLIST(model);
            }
          });
          this.SET_MODELLIST({
            sImg: data.img_web_url,
            bImg: data.img_web_url,
            data: JSON.parse(data.data),
            user: true,
            id: data.id, // model 对象有id表示是否跟用户绑定了
          });
        }
      } else {
        const model = localStorage.getItem('voo-model');
        if (model && JSON.parse(model)) {
          this.SET_MODELLIST(JSON.parse(model));
        }
      }
    } catch (error) {
      console.log(error);
    }
  },
  async getKlarnaSave() {
    try {
      const { data } = await api.common.klarnaSave();
      // 字符串转成数字，方便后续使用
      ['reduce_for_klarna_pay_reduce_money', 'reduce_for_klarna_pay_condition_money'].forEach(
        (key) => {
          data[key] = +data[key];
        },
      );
      this.klarnaSave = data;
    } catch (error) {
      console.log(error);
      this.klarnaSave = {};
    }
  },
  setTabClick(val: boolean) {
    this.SET_TAB_CLICK(val);
  },
  setThirdPartyLoginreRequest(val: number) {
    this.SET_THIRD_PARTY_LOGIN_REQUEST(val);
  },
  setVideoPlay(val: boolean) {
    this.videoPlay = val;
  },
  setAlreadyShowErr(val: boolean) {
    this.SET_ALREADY_SHOW_ERR(val);
  },

  /**
   * 获取魔方配置的可用的支付方式. 仅 nav.vue price-difference.vue buy-vomer-address.vue 3个组件用
   * @param {*} param
   */
  async getAllowPaymentMethods() {
    const allowPayMethods = [];
    const defaultallowPayMethods = [
      // klarna klarna_adyen 支付默认关闭。需要接口提供支持的国家才能向后运行
      PAY_METHODS.PAYPAL, // paypay 支付默认开启
      PAY_METHODS.ADYEN, // adyen 支付默认开启
      PAY_METHODS.AFTER_PAY, // afterpay 支付默认开启
    ];
    try {
      const { data, code } = await api.pay.payCenter.paymentMethodsList();
      const list = data?.payment_methods;
      if (code === 200 && list?.length) {
        for (let i = 0; i < list.length; i++) {
          allowPayMethods.push(list[i].payment_method);
        }
        this.SET_ALLOW_PAY_METHODS(allowPayMethods);
        return;
      }
    } catch (error) {
      console.error(error);
    }
    this.SET_ALLOW_PAY_METHODS(defaultallowPayMethods);
  },
  async getRegisterImg() {
    try {
      const data = await this.getBlock({ block_type: 13 });
      if (data && data.register_popup && data.register_popup.pc_image) {
        this.SET_REGISTER_DATA({
          registerImg: data.register_popup.pc_image,
          pc_jump_target: data.register_popup.pc_jump_target,
        });
      }
    } catch (error) {
      console.log('getRegisterImg error', error);
    }
  },
  async getPublicConfig() {
    try {
      const {
        data: {
          info: {
            currency,
            product_config,
            rule_flag,
            system_timestamp,
            ongoing_lottery,
            show_my_card,
            crm_point,
            prescription_recognition_open,
          },
          config_names: { product_detail_pop_map },
          blocks: {
            104: { floating_window },
          },
          json_values: { change_email_switch, seel },
        },
      } = await api.common.getPublicResources({
        info: [
          {
            key: 'currency',
          },
          {
            key: 'product_config',
          },
          {
            key: 'rule_flag',
          },
          {
            key: 'system_timestamp',
          },
          {
            key: 'ongoing_lottery',
          },
          {
            key: 'show_my_card',
          },
          {
            key: 'crm_point',
          },
          {
            key: 'prescription_recognition_open',
          },
        ],
        config_names: ['product_detail_pop_map'],
        blocks: [
          {
            block_type: 104,
          },
        ],
        json_values: ['change_email_switch', 'seel'],
      });
      if (currency?.length) {
        this.setCurrencyList(currency);
      }
      if (product_config) {
        this.SET_PRODUCT_CONFIG(product_config);
      }
      if (rule_flag && rule_flag.rule_flag == 1) {
        try {
          this.halfPriceRule = rule_flag;
        } catch (error) {
          console.error('getPublicConfig rule_flag error', error);
        }
      }
      if (system_timestamp) {
        this.SET_CURRENT_YEAR(system_timestamp);
        this.SET_SYSTEM_TIMESTAMP(system_timestamp);
      }
      if (ongoing_lottery) {
        this.SET_ONGOING_LOTTERY(ongoing_lottery);
      }
      if (floating_window) {
        this.SET_FLOAT_WINDOW(floating_window);
      }
      if (product_detail_pop_map) {
        this.SET_PRODUCT_DETAIL_POP_MAP(product_detail_pop_map);
      }
      // if (register_coupon_popup) {
      //   this.SET_REGISTER_COUPON_POPUP(register_coupon_popup);
      // }
      this.show_my_card = show_my_card || 0;
      if (crm_point) {
        this.SET_CRM_POINT_RULE(crm_point);
      }
      // 处方识别
      if (prescription_recognition_open) {
        const {
          prescription_recognition_open: open,
          prescription_recognition_novice_guide_open: guideOpen,
          timeout_time: loadAnimationTime,
        } = prescription_recognition_open;
        this.SET_PR_SWITCH({
          open,
          guideOpen,
          loadAnimationTime,
        });
      }
      if (change_email_switch?.json_value) {
        this.SET_CHANGE_EMAIL_SWITCH(
          change_email_switch.json_value.change_email_switch_pc || 'off',
        );
        this.SET_VERIFY_EMAIL_SWITCH(
          change_email_switch.json_value.verify_email_switch_pc || 'off',
        );
      }
      if (seel?.json_value) {
        this.SET_SEEL(seel.json_value.state || 2);
      }
    } catch (error) {
      console.error('getPublicConfig', error);
    }
  },
  /**
   * 应用 BFF pc_global 接口返回的数据到 store
   * @param bffData - POST:/api/bff/pc_global 的响应数据
   */
  applyBffPcGlobalData(bffData: ApplyBffPcGlobalDataPayload | null | undefined) {
    if (!bffData) return;

    const { data } = bffData;
    if (!data) return;

    // 币种列表（OpenAPI 将 currency 标成单体；运行时为列表）
    const currencyList = data.currency;
    if (Array.isArray(currencyList) && currencyList.length) {
      this.setCurrencyList(currencyList);
    }

    // 第二件规则（OpenAPI 中 discount_ratio 仅 null；与 store 的 SecondDiscountRule 不一致）
    if (data.rule_flag?.rule_flag == 1) {
      this.halfPriceRule = data.rule_flag as SecondDiscountRule;
    }

    // 系统时间戳 -> 当前年份 + 原始秒（顶通等）
    if (data.system_timestamp) {
      this.SET_CURRENT_YEAR(data.system_timestamp);
      this.SET_SYSTEM_TIMESTAMP(data.system_timestamp);
    }

    // 进行中的抽奖活动
    if (data.ongoing_lottery) {
      this.SET_ONGOING_LOTTERY(data.ongoing_lottery);
    }

    // 浮窗配置
    if (data.floating_window) {
      this.SET_FLOAT_WINDOW(data.floating_window);
    }

    // CRM 积分配置
    if (data.crm_point) {
      this.SET_CRM_POINT_RULE(data.crm_point);
    }
    if (data.country) {
      this.SET_COUNTRY_LIST(data.country);
    }

    // 处方识别开关（OpenAPI 标为 number；运行时为配置对象）
    const prSwitchPayload = data.prescription_recognition_open as unknown;
    if (prSwitchPayload && typeof prSwitchPayload === 'object') {
      const {
        prescription_recognition_open: open,
        prescription_recognition_novice_guide_open: guideOpen,
        timeout_time: loadAnimationTime,
      } = prSwitchPayload as {
        prescription_recognition_open?: number;
        prescription_recognition_novice_guide_open?: number;
        timeout_time?: number;
      };
      this.SET_PR_SWITCH({
        open,
        guideOpen,
        loadAnimationTime,
      });
    }

    // 用户收藏列表
    if (data.all_wish_ids && Array.isArray(data.all_wish_ids)) {
      this.allWishIds = data.all_wish_ids;
    }

    // json_values（OpenAPI 仅 key 占位；实际为各魔方键对象）
    const jsonValues = data.json_values as Record<string, any> | null | undefined;
    if (jsonValues) {
      // change_email_switch
      if (jsonValues.change_email_switch) {
        this.SET_CHANGE_EMAIL_SWITCH(
          jsonValues.change_email_switch.change_email_switch_pc || 'off',
        );
        this.SET_VERIFY_EMAIL_SWITCH(
          jsonValues.change_email_switch.verify_email_switch_pc || 'off',
        );
      }

      // seel
      if (jsonValues.seel) {
        this.SET_SEEL(jsonValues.seel.state || 2);
      }

      // product_detail_pop_map
      if ((data as any)?.product_detail_pop_map) {
        this.SET_PRODUCT_DETAIL_POP_MAP((data as any).product_detail_pop_map);
      }

      // trend_book_right_nav
      if (jsonValues.trend_book_right_nav?.pc) {
        this.SET_TREND_RIGHT_NAV(jsonValues.trend_book_right_nav.pc);
      }
    }

    // 试戴模特图数据 (映射为 modelList 格式)
    if (data.tryon_img && Array.isArray(data.tryon_img)) {
      const list = data.tryon_img.map((item: BffTryonImgItem) => ({
        sImg: item.img_url,
        bImg: item.img_url,
        data: item.data,
      }));
      this.SET_MODEL_TEMPLATE_LIST(list);
    }
  },
  async getPublicResources(requestData: any) {
    try {
      const { data } = await api.common.getPublicResources(requestData);
      return data;
    } catch (error) {
      console.error(error);
    }
  },
  async getNewRegisterCoupon(params?: any) {
    try {
      const {
        data: {
          blocks: {
            103: { register_coupon_popup },
          },
          tactics,
        },
      } = await api.common.getPublicResources({
        blocks: [{ block_type: '103' }],
        url_utm_info: params?.url_utm_info,
        utm_info: this.getUtmInfo(),
      });
      if (tactics) {
        this.SET_TACTICS(tactics);
      }
      return { register_coupon_popup };
    } catch (error) {
      console.log(error);
    }
  },
  async getLRDialogConfig(data?: any) {
    try {
      const {
        data: { tactics },
      } = await api.common.getPublicResources({
        utm_info: this.getUtmInfo(data?.path || 'fast_register'),
      });
      if (tactics) {
        this.SET_TACTICS(tactics);
      }
    } catch (error) {
      console.log('getLRDialogConfig error', error);
    }
  },
  async getStateLotteryInfo(id: number | string) {
    if (id == 0 || !id) return;
    const { data } = await api.activity.commonLotteryInfo({
      id,
    });
    this.SET_STATE_LOTTERY_INFO(data);
  },
  async getUserAllWishIds() {
    try {
      if (!this.token) {
        this.allWishIds = [];
        return;
      }
      const { data } = await api.account.wish.allWishIds();
      if (Array.isArray(data)) {
        this.allWishIds = data;
      } else {
        this.allWishIds = [];
      }
    } catch (error) {
      console.error('getUserAllWishIds', error);
    }
  },

  // 原 mutations.js
  SET_VISITORID(value) {
    this.visitorId = value;
    localStorage.setItem('voo-visitorId', value);
  },
  SET_REWARD(reward) {
    this.reward = reward;
    localStorage.setItem('voo-reward', JSON.stringify(reward));
  },
  SET_CARTINFOR(infor) {
    this.cartInfor = infor;
    // 存储在local里的key改为 voo-cartInformation，避免跟之前的相同
    localStorage.setItem('voo-cartInformation', JSON.stringify(infor));
  },
  SET_SCROLLHASH(hash) {
    this.scrollHash = hash;
  },
  SET_SCROLLTOP(val) {
    this.scrollTop = val;
  },
  SET_LENSDATA(data) {
    this.lensData = data;
    localStorage.setItem('voo-lensData', JSON.stringify(this.lensData));
  },
  SET_MYPRESDATA(result) {
    this.myPresData = result;
    localStorage.setItem('voo-myPresData', JSON.stringify(result));
  },
  SET_PAYFLAG(val) {
    this.payFlag = val;
    localStorage.setItem('voo-payFlag', val);
  },
  SET_ACTIVITYCLOSE(flag) {
    this.activityClose = flag; // activityClose表示魔方是否配置了顶通，true表示配置了，false表示未配置
  },
  SET_ACTIVITYFLAG(flag) {
    this.activityFlag = flag;
  },
  SET_MODEL_TEMPLATE_LIST(list) {
    this.modelList = list;
  },
  SET_MODELLIST(model) {
    // 只允许出现一张上传的照片，这里统一处理
    const firstModel = this.modelList[0];
    if (firstModel && firstModel.user) {
      this.modelList.shift();
    }
    // 未绑定用户的试戴图片存到local中
    if (!model.id) {
      setLocal('voo-model', model);
    }
    this.modelList.unshift(model);
  },
  DEL_MODELLIST(model) {
    const index = this.modelList.indexOf(model);
    this.modelList.splice(index, 1);
    // 只能删除用户上传的照片，且仅支持上传1张，所以删除的时候需要清空一下本地缓存
    localStorage.removeItem('voo-model');
  },
  SET_PAY_ERROR_MSG(msg) {
    if (typeof msg !== 'string' || msg === 'undefined' || msg === 'NaN') {
      msg = '';
    }
    this.payErrorMsg = msg;
    setLocal('voo-payErrorMsg', this.payErrorMsg);
  },
  SET_FILTERFIXED(top) {
    this.filterFixed = top;
  },
  SET_TOKEN(token, isInDefaultLayout = false) {
    this.token = token;
    setLocal('voo-token', token);
    if (process.client && token) Cookies.set('voo-token', token, { expires: 365 });
    if (!isInDefaultLayout) {
      this.getUserAllWishIds?.();
    }
  },
  SET_UNCHECK_LIST(list) {
    this.uncheckList = list;
    setSession('voo-uncheckList', list);
  },
  SET_CURRENCY_LIST(list) {
    this.currencyList = list;
  },
  SET_CURRENCY(obj) {
    this.currency = obj;
    setLocal('voo-currency', obj);
    if (process.client && obj.name) Cookies.set('voo-currency', obj.name, { expires: 365 });
  },
  SET_BFF_SSR_DATA(payload: Partial<BffLayoutSsrState>) {
    this.bffSsrData = { ...this.bffSsrData, ...payload };
  },
  SET_BFF_CSR_DATA(payload: Partial<BffLayoutCsrState>) {
    this.bffCsrData = { ...this.bffCsrData, ...payload };
  },
  SET_PRODUCT_CONFIG(obj) {
    this.productConfig = obj;
  },
  SET_COUNTRY_LIST(list) {
    this.countryList = list;
  },
  SET_PRESCRIPTION_LIST(list) {
    this.prescriptionList = list;
  },
  // 设置系统年份
  SET_CURRENT_YEAR(timeStamp) {
    const fiveHour = 5 * 60 * 60;
    const currentYear = new Date((timeStamp - fiveHour) * 1000).getUTCFullYear();
    this.currentYear = currentYear;
  },
  /** 服务器 Unix 秒，与 BFF `system_timestamp` 一致 */
  SET_SYSTEM_TIMESTAMP(sec) {
    this.systemTimestamp = typeof sec === 'number' && Number.isFinite(sec) ? Math.floor(sec) : null;
  },
  // 设置抽奖活动
  SET_ONGOING_LOTTERY(obj) {
    this.onGoingLottery = obj;
  },
  // 快捷注册弹窗图标配置
  SET_FLOAT_WINDOW(obj) {
    this.floatingWindow = obj;
  },
  // 快捷注册弹窗内容配置
  SET_REGISTER_COUPON_POPUP(obj) {
    this.registerCouponPopup = obj;
  },
  // 权益发放策略
  SET_TACTICS(obj) {
    this.tactics = obj;
  },
  SET_ADDRESS_LIST(list) {
    this.addressList = list;
  },
  SET_USER_INFO(info) {
    this.userInfo = info;
    this.isVip = info.type === 2; // 用户类型 1普通用户 2VIP用户
    setLocal('voo-userInfo', info);
    /* 三方统计用到了 userId，这里额外存一份 */
    this.userId = String(info.id || '');
    setLocal('voo-userId', this.userId);
    /**
     * @TrackType sensorsdata
     * @EventType sign_up
     * @EventDesc 神策登录事件
     * @EventParams {
     *  uuid: "用户的 UUID",
     * }
     * @Tapd 1009440
     */
    if (info.uuid) {
      window.voo_sensors?.login?.(info.uuid);
    }

    // 设置 Facebook 用户数据
    if (process.client && window?.fbq && info?.id) {
      try {
        updateUserData();
      } catch (error) {
        console.error('fbq user data set error-- 设置用户数据失败', error);
      }
    }
  },
  SET_SURVEY_LIST(list) {
    this.surveyList = list;
  },
  SET_SURVEY_POP_INFO(obj) {
    this.surveyPopInfo = obj;
  },
  SET_SURVEY_FINAL_INFO(obj) {
    this.surveyFinalInfo = obj;
    setLocal('voo-surveyFinalInfo', obj);
  },
  SET_SURVEY_DETAIL_SHOW(val) {
    this.questionDetailShow = val;
  },
  SET_FORGOT_PASSWORD_SHOW(val) {
    this.ForgotPasswordShow = val;
  },
  SET_IS_FROM_RESET_PASSWORD(val) {
    this.isFromResetPassword = val;
  },
  SET_IS_LRDIALOG_SHOW(val) {
    this.isLRDialogShow = val;
  },
  SET_FORGOT_DIALOG_EMAIL(val) {
    this.forgotDialogEmail = val;
  },
  SET_CRM_POINT_RULE(data) {
    this.crmPointRule = data;
  },
  SET_PRODUCT_DETAIL_POP_MAP(info) {
    this.productDetailPopMap = info;
  },
  SET_VIP_EQUITY(data) {
    this.vipEquity = data;
  },
  SET_ORDER_NUM(data) {
    this.orderNum = data;
  },
  SET_REGISTER_DATA(data) {
    this.registerData = data;
  },
  HANDLE_TRACKORDER_MASK(flag) {
    this.showTrackOrderMask = flag;
  },
  SET_COUNT_DOWN_COUPON(data) {
    this.countTimeCoupon = data;
  },
  SET_TAB_CLICK(val) {
    this.tabClick = val;
  },
  SET_THIRD_PARTY_LOGIN_REQUEST(val) {
    this.thirdPartyLoginreRequest = val;
  },
  SET_FROM_ROUTE_NAME(val) {
    this.fromRouteName = val;
  },
  SET_FROM_ROUTE_CN_NAME(val) {
    this.fromRouteCnName = val;
  },
  SET_DMP_PAYMENT_UUID(val) {
    this.DMP_PAYMENT_UUID = val;
    setLocal('voo-DMP_PAYMENT_UUID', val);
  },
  SET_UUID_ORDER_LIST(list) {
    this.uuidOrderList = list;
    setLocal('voo-uuidOrderList', list);
  },
  SET_PAGE_IS_CHANGE(val) {
    this.pageIsChange = val;
  },
  SET_ALREADY_SHOW_ERR(val) {
    this.AlreadyShowErr = val;
  },
  SET_CART_NORMAL_LR(val) {
    this.cartNormalLR = val;
  },
  SET_ALLOW_PAY_METHODS(val) {
    // 设置允许的支付方式
    this.allowPayMethods = val;
  },
  // 设置盲人模式
  SET_BLIND_MODE(payload) {
    if (payload) {
      // 给页面的body加上blind-mode的id属性
      document.body.classList.add('blind-mode');
      document.getElementsByTagName('body')[0].style.setProperty('--origin', '#cc1f1f');
    } else {
      document.body.classList.remove('blind-mode');
      document.getElementsByTagName('body')[0].style.setProperty('--origin', '#FE5000');
    }
    this.blindMode = payload;
    setLocal('voo-blindMode', payload);
  },
  SET_HOME_COUPON_SHOW(payload) {
    this.homeCouponShow = payload;
  },
  UPDATE_COMMON_COUNTRY(list) {
    this.commonCountry = list;
  },
  UPDATE_IS_ROUTER_PUSH(flag) {
    this.isRouterPush = flag;
  },
  SET_FRAME_SIZE_UNIT(frameSizeUnit) {
    this.frameSizeUnitInStore = frameSizeUnit;
    setLocal('voo-frame-size-unit', frameSizeUnit);
  },
  SET_SCROLL_ICON_HIDE(val) {
    this.scrollIconHide = val;
  },
  SET_DESKTYPE(val) {
    this.deskType = val;
  },
  SET_HASREFRESHCART(val) {
    this.hasRefreshCart = val;
  },
  SET_STATE_LOTTERY_INFO(val) {
    this.stateLotteryInfo = val;
  },
  SET_STOP_WATCH_ROUTE(val) {
    this.stopWatchRoute = val;
  },
  SET_PR_SWITCH(val) {
    this.prSwitch = val;
  },
  // SET_SHOW_SCAN_GUIDE(val) {
  //   this.showScanGuideMask = val;
  // },
  SET_SCAN_INFO(val) {
    this.scanInfo = val;
  },
  SET_CHANGE_EMAIL_SWITCH(val) {
    this.changeEmailSwitch = val;
  },
  SET_VERIFY_EMAIL_SWITCH(val) {
    this.verifyEmailSwitch = val;
  },
  SET_TREND_RIGHT_NAV(val) {
    this.trendRightNav = val;
  },
  SET_IS_CUSTOMIZE_GLASSES_SHOW(val) {
    this.isCustomizeGlassesShow = val;
  },
  SET_IS_RIGHT_NAV_DISPLAY(val) {
    this.isRightNavDisplay = val;
  },
  SET_FESTIVAL_QUIZ_SHOW_FOOTER(val) {
    this.festivalQuizShowFooter = val;
  },
  SET_CART_PRICE_INFO(val) {
    this.cartPriceInfo = val;
  },
  SET_IS_VISITED_CART(val) {
    this.isVisitedCart = val;
  },
  SET_SHOULD_ALERT_SEND_GIFT(val) {
    this.shouldAlertSendGift = val;
  },
  SET_IS_FIRST_ENTER_CART(val) {
    this.isFirstEnterCart = val;
  },
  SET_HISTORY_FREE_GIFT_CART_ID(val) {
    this.historyFreeGiftCartId = val;
  },
  SET_NAVIGATION_TYPE(val) {
    this.navigationInfo.navigationType = val;
  },
  PUSH_NAVIGATION_HISTORY() {
    if (
      this.navigationInfo.navigationHistory?.has(window.history.state.position) &&
      this.navigationInfo.navigationType !== 'push' &&
      this.navigationInfo.navigationHistory?.get(window.history.state.position)?.href ===
        window.location.href
    ) {
      return;
    }
    const key = randomString();
    this.navigationInfo.navigationHistory?.set(window.history.state.position, {
      href: window.location.href,
      key,
    });
    if (this.navigationInfo.navigationType === 'push') {
      this.navigationInfo.navigationHistory?.forEach((value: any, _key: number) => {
        if (_key > window.history.state.position) {
          this.navigationInfo.navigationHistory?.delete(_key);
          this.navigationInfo.pageStates?.delete(value.key);
        }
      });
    }
  },
  SET_NAVIGATION_PAGE_STATE(state) {
    let key = this.navigationInfo.navigationHistory?.get(window.history.state.position)?.key;
    if (!key) {
      this.PUSH_NAVIGATION_HISTORY?.();
      // 重新获取刚刚创建的 key
      key = this.navigationInfo.navigationHistory?.get(window.history.state.position)?.key;
    }
    if (key) {
      this.navigationInfo.pageStates?.set(key, {
        ...state,
        href: window.location.href,
      });
    }
    const pageStatesSize = this.navigationInfo.pageStates?.size || 0;
    if (pageStatesSize > 20) {
      const firstPageStateKey = this.navigationInfo.pageStates?.keys().next().value;
      const firstNavHistoryKey = this.navigationInfo.navigationHistory?.keys().next().value;
      if (firstPageStateKey) {
        this.navigationInfo.pageStates?.delete(firstPageStateKey);
      }
      if (firstNavHistoryKey !== undefined) {
        this.navigationInfo.navigationHistory?.delete(firstNavHistoryKey);
      }
    }
  },
  GET_NAVIGATION_PAGE_STATE() {
    const key = this.navigationInfo.navigationHistory?.get(window.history.state.position)?.key;
    if (!key) {
      return null;
    }
    const pageState = this.navigationInfo.pageStates?.get(key);
    if (pageState) {
      // 如果 href 完全匹配，说明是回到了原来的页面，应该恢复状态
      // 不需要严格检查 navigationType，因为后退操作完成后可能已经被重置为 'push'
      if (pageState.href === window.location.href) {
        return pageState;
      } else {
        this.REMOVE_NAVIGATION_PAGE_STATE?.(key);
      }
    }
    return null;
  },
  REMOVE_NAVIGATION_PAGE_STATE(key) {
    this.navigationInfo.pageStates?.delete(key);
  },

  SET_IS_RIGHT_NAV_CLICK(val) {
    this.isRightNavClick = val;
  },
  // Review 获取优惠券弹窗
  SET_REVIEW_GET_COUPON_DIALOG(show, couponList = []) {
    this.reviewGetCouponDialog = {
      show,
      couponList: show ? couponList : [],
    };
  },
  SET_SEEL(val) {
    this.seel = val;
  },
  SET_SHOW_MY_CARD(val) {
    this.show_my_card = val;
  },
};

export default actions;
